import { useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { Modal } from "../../../components/core/basic/Modal";
import { useUpload } from "../../uploads/api/useUpload";
import { StatusBadge } from "../../uploads/StatusBadge";
import { UploadStatus, isTerminalStatus } from "../../uploads/constants";
import { successToast } from "../../../utils/toasts";
import { UploadStep } from "./UploadStep";
import { ReviewStep } from "./ReviewStep";
import { CommitStep } from "./CommitStep";

export const PricingPlanBulkUploadModalV2 = NiceModal.create(({ uploadId }: { uploadId?: number }) => {
  const modal = useModal();
  const { t } = useTranslation(["pricingv2"]);
  const [activeUploadId, setActiveUploadId] = useState(uploadId);
  const { data: upload } = useUpload(activeUploadId);

  const isProcessing = !!activeUploadId && !!upload && !isTerminalStatus(upload.status);

  const showReview = upload?.status === UploadStatus.VALIDATED;
  const showCommit =
    upload?.status === UploadStatus.PROCESSING ||
    upload?.status === UploadStatus.DONE ||
    upload?.status === UploadStatus.PARTIAL;
  const showValidationFailed = upload?.status === UploadStatus.VALIDATION_FAILED;
  const showValidating = !showReview && !showCommit && !showValidationFailed && upload?.status !== undefined;

  return (
    <Modal
      isOpen={modal.visible}
      isDismissable
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          if (isProcessing) {
            successToast({
              title: t(($) => $.pricingv2["Upload continues in the background"]),
              message: (
                <span>
                  <Trans
                    t={t}
                    i18nKey={($) => $.pricingv2["Track progress on the <uploadsLink>Uploads page</uploadsLink>."]}
                    components={{
                      uploadsLink: <Link to="/user/uploads" className={css({ textDecoration: "underline" })} />,
                    }}
                  />
                </span>
              ),
            });
          }
          modal.remove();
        }
      }}
      title={
        <HStack gap="medium" alignItems="center">
          <span>{t(($) => $.pricingv2["Bulk Upload Pricing Plans"])}</span>
          {upload && <StatusBadge status={upload.status} />}
        </HStack>
      }
      css={{ minWidth: "1000px" }}
    >
      <VStack
        gap="large"
        alignItems="stretch"
        paddingY="xlarge"
        paddingX="xlarge"
        overflow="auto"
        minHeight="0"
        flex="1"
      >
        {!activeUploadId && <UploadStep onUploadCreated={setActiveUploadId} />}
        {activeUploadId && upload && showValidating && (
          <span className={css({ color: "gray.600" })}>{t(($) => $.pricingv2["Validating your upload…"])}</span>
        )}
        {activeUploadId && upload && showValidationFailed && (
          <VStack gap="medium" alignItems="start">
            <span className={css({ color: "red.700", fontWeight: "bold" })}>
              {t(($) => $.pricingv2["Validation failed."])}
            </span>
            <span className={css({ color: "gray.600" })}>
              {t(($) => $.pricingv2["The CSV could not be processed. Check the file for issues and re-upload."])}
            </span>
          </VStack>
        )}
        {activeUploadId && upload && showReview && <ReviewStep upload={upload} onCommitStarted={() => undefined} />}
        {activeUploadId && upload && showCommit && <CommitStep upload={upload} onClose={() => modal.remove()} />}
      </VStack>
    </Modal>
  );
});
